import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Search, ArrowRight, Trophy,
  PartyPopper, CalendarIcon, Check,
} from 'lucide-react';
import { format } from 'date-fns';
import PhaseIntroCard from '@/features/dashboard/PhaseIntroCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/features/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';

const Phase5 = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  // Deal modal state
  const [dealOpen,       setDealOpen]       = useState(false);
  const [address,        setAddress]        = useState('');
  const [purchasePrice,  setPurchasePrice]  = useState('');
  const [closeDate,      setCloseDate]      = useState<Date>();
  const [submitting,     setSubmitting]     = useState(false);
  const [dealStep,       setDealStep]       = useState(1);
  const [strategy,       setStrategy]       = useState('');
  const [shippingStreet, setShippingStreet] = useState('');
  const [shippingCity,   setShippingCity]   = useState('');
  const [shippingState,  setShippingState]  = useState('');
  const [shippingZip,    setShippingZip]    = useState('');
  const [instrumental,   setInstrumental]   = useState('');
  const [struggled,      setStruggled]      = useState('');
  const [step1Errors,    setStep1Errors]    = useState<Record<string, string>>({});
  const [step2Errors,    setStep2Errors]    = useState<Record<string, string>>({});
  const [step3Errors,    setStep3Errors]    = useState<Record<string, string>>({});
  const dealFormRef = useRef<HTMLDivElement>(null);

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = dealFormRef.current?.querySelector('.border-red-400') as HTMLElement | null;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const resetDealModal = () => {
    setDealStep(1);
    setAddress('');
    setPurchasePrice('');
    setCloseDate(undefined);
    setStrategy('');
    setShippingStreet('');
    setShippingCity('');
    setShippingState('');
    setShippingZip('');
    setInstrumental('');
    setStruggled('');
    setStep1Errors({});
    setStep2Errors({});
    setStep3Errors({});
  };

  const validateStep1 = () => {
    const errors: Record<string, string> = {};
    if (!purchasePrice || parseFloat(purchasePrice) <= 0) errors.purchasePrice = 'Purchase price is required';
    if (!closeDate) errors.closeDate = 'Close date is required';
    if (!strategy) errors.strategy = 'Strategy is required';
    setStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors: Record<string, string> = {};
    if (!shippingStreet.trim()) errors.shippingStreet = 'Street address is required';
    if (!shippingCity.trim()) errors.shippingCity = 'City is required';
    if (!shippingState.trim() || shippingState.trim().length !== 2) errors.shippingState = 'State (2 letters) is required';
    if (!shippingZip.trim() || !/^\d{5}(-\d{4})?$/.test(shippingZip.trim())) errors.shippingZip = 'Valid ZIP code is required';
    setStep2Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep3 = () => {
    const errors: Record<string, string> = {};
    if (instrumental.trim().length < 20) errors.instrumental = 'Please share at least 20 characters';
    if (struggled.trim().length < 20) errors.struggled = 'Please share at least 20 characters';
    setStep3Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDealSubmit = async () => {
    if (!session?.user?.id) { toast.error('You must be logged in.'); return; }
    setSubmitting(true);
    const parsedPrice = purchasePrice ? parseFloat(purchasePrice.replace(/[^0-9.]/g, '')) : null;
    const { error } = await supabase.from('first_deals').insert({
      user_id: session.user.id,
      property_address: address.trim(),
      expected_close_date: closeDate ? format(closeDate, 'yyyy-MM-dd') : null,
      strategy_type: strategy,
      purchase_price: parsedPrice && !isNaN(parsedPrice) ? parsedPrice : null,
      shipping_street: shippingStreet.trim(),
      shipping_city: shippingCity.trim(),
      shipping_state: shippingState.trim().toUpperCase(),
      shipping_zip: shippingZip.trim(),
      instrumental_text: instrumental.trim(),
      struggled_text: struggled.trim(),
    });
    setSubmitting(false);
    if (error) { toast.error('Something went wrong saving your deal.'); return; }
    setDealOpen(false);
    resetDealModal();
    toast.success('Congrats on closing your first deal!');
    setTimeout(() => {
      toast('Achievement unlocked: Deal Closer!', {
        description: 'Visit your profile to see your new award.',
      });
    }, 800);
  };

  const cards = [
    {
      icon: Shield,
      title: 'Negotiation Support',
      items: ['Review offer strategy', 'Fine-tune numbers', 'Structure terms', 'Handle counters', 'Protect contract'],
    },
    {
      icon: Search,
      title: 'Escrow & Inspection Support',
      items: ['Due diligence', 'Red flags', 'Renegotiation', 'Appraisal issues', 'Deadline tracking'],
    },
  ];

  return (
    <div>
      <PhaseIntroCard
        phase={5}
        title="First Deal"
        icon={Trophy}
        description="You're under contract. This is where the real work begins. Lean on your mentors to help navigate inspection, financing, and closing. The goal isn't just keys in hand. It's the proof that you can do this, and the confidence to do it again."
      />

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mb-10">
        {cards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
            className="p-6 rounded-lg border border-gold/20 bg-card shadow-card hover:border-gold/40 hover:-translate-y-[3px] hover:shadow-card-hover transition-[border-color,box-shadow,transform] duration-200"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full gradient-gold flex items-center justify-center">
                <card.icon className="h-5 w-5 text-primary-foreground" />
              </div>
              <h3 className="font-serif text-xl font-bold text-foreground">{card.title}</h3>
            </div>

            <ul className="space-y-2.5 mb-6">
              {card.items.map((item, j) => (
                <motion.li
                  key={j}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 + j * 0.035 + 0.12, duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {item}
                </motion.li>
              ))}
            </ul>

            <button
              onClick={() => navigate('/mentors')}
              className="w-full flex items-center justify-center gap-2 py-3 gradient-gold text-primary-foreground font-semibold rounded-md shadow-gold hover:opacity-90 transition-[transform,opacity] duration-150 active:scale-[0.97]"
            >
              Book Mentor Support <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        ))}
      </div>

      {/* ── Closed the Deal ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <button
          onClick={() => setDealOpen(true)}
          className="flex items-center gap-3 px-6 py-4 rounded-xl gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 transition-opacity text-base"
        >
          <PartyPopper className="h-5 w-5 shrink-0" />
          I closed the deal
        </button>
        <p className="mt-2 text-sm text-muted-foreground">
          Tell us when you close the deal so we can send you something to celebrate.
        </p>
      </motion.div>

      {/* Deal dialog */}
      <Dialog open={dealOpen} onOpenChange={(open) => { if (!open) resetDealModal(); setDealOpen(open); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              I Closed a Deal!
            </DialogTitle>
            <DialogDescription>
              {dealStep === 1
                ? 'Tell us about your deal.'
                : dealStep === 2
                ? 'Where should we send your celebration package?'
                : 'Share a little about your journey -- this helps future members.'}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="pt-1 pb-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Step {dealStep} of 3</span>
              <span className="text-xs text-muted-foreground">
                {dealStep === 1 ? 'Deal Details' : dealStep === 2 ? 'Shipping Address' : 'Your Story'}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full gradient-gold transition-all duration-300"
                style={{ width: dealStep === 1 ? '33%' : dealStep === 2 ? '66%' : '100%' }}
              />
            </div>
          </div>

          {/* Scrollable form body */}
          <div ref={dealFormRef} className="overflow-y-auto flex-1 px-1">

            {/* Step 1: Deal Details */}
            {dealStep === 1 && (
              <div className="space-y-4 pt-1">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Property Address <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <Input
                    placeholder="123 Main St, City, State"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Purchase Price</label>
                    <Input
                      placeholder="e.g. 250000"
                      value={purchasePrice}
                      onChange={e => { setPurchasePrice(e.target.value); if (step1Errors.purchasePrice) setStep1Errors(p => ({ ...p, purchasePrice: '' })); }}
                      type="number"
                      min="0"
                      className={cn(step1Errors.purchasePrice && 'border-red-400 focus-visible:ring-red-400')}
                    />
                    {step1Errors.purchasePrice && <p className="text-xs text-red-500 mt-1">{step1Errors.purchasePrice}</p>}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Close Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !closeDate && 'text-muted-foreground',
                            step1Errors.closeDate && 'border-red-400',
                          )}
                          onClick={() => { if (step1Errors.closeDate) setStep1Errors(p => ({ ...p, closeDate: '' })); }}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {closeDate ? format(closeDate, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarPicker mode="single" selected={closeDate} onSelect={setCloseDate} initialFocus className={cn('p-3 pointer-events-auto')} />
                      </PopoverContent>
                    </Popover>
                    {step1Errors.closeDate && <p className="text-xs text-red-500 mt-1">{step1Errors.closeDate}</p>}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Strategy</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['Buy & Hold', 'Fix & Flip', 'BRRRR', 'Short-Term Rental', 'House Hack', 'Other'] as const).map(s => (
                      <button
                        key={s}
                        onClick={() => { setStrategy(s); if (step1Errors.strategy) setStep1Errors(p => ({ ...p, strategy: '' })); }}
                        className={cn(
                          'px-3 py-2 rounded-lg border text-sm font-medium transition-all text-left',
                          strategy === s
                            ? 'gradient-gold text-primary-foreground border-transparent shadow-gold'
                            : 'border-border text-foreground hover:border-primary/40',
                          step1Errors.strategy && strategy !== s && 'border-red-200',
                        )}
                      >{s}</button>
                    ))}
                  </div>
                  {step1Errors.strategy && <p className="text-xs text-red-500 mt-1">{step1Errors.strategy}</p>}
                </div>
              </div>
            )}

            {/* Step 2: Shipping Address */}
            {dealStep === 2 && (
              <div className="space-y-3 pt-1">
                <p className="text-sm text-muted-foreground">Where should we send your celebration package?</p>
                <div>
                  <Input
                    placeholder="Street Address"
                    value={shippingStreet}
                    onChange={e => { setShippingStreet(e.target.value); if (step2Errors.shippingStreet) setStep2Errors(p => ({ ...p, shippingStreet: '' })); }}
                    className={cn(step2Errors.shippingStreet && 'border-red-400 focus-visible:ring-red-400')}
                  />
                  {step2Errors.shippingStreet && <p className="text-xs text-red-500 mt-1">{step2Errors.shippingStreet}</p>}
                </div>
                <div>
                  <Input
                    placeholder="City"
                    value={shippingCity}
                    onChange={e => { setShippingCity(e.target.value); if (step2Errors.shippingCity) setStep2Errors(p => ({ ...p, shippingCity: '' })); }}
                    className={cn(step2Errors.shippingCity && 'border-red-400 focus-visible:ring-red-400')}
                  />
                  {step2Errors.shippingCity && <p className="text-xs text-red-500 mt-1">{step2Errors.shippingCity}</p>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      placeholder="State (e.g. CO)"
                      value={shippingState}
                      onChange={e => { setShippingState(e.target.value.slice(0, 2)); if (step2Errors.shippingState) setStep2Errors(p => ({ ...p, shippingState: '' })); }}
                      maxLength={2}
                      className={cn(step2Errors.shippingState && 'border-red-400 focus-visible:ring-red-400')}
                    />
                    {step2Errors.shippingState && <p className="text-xs text-red-500 mt-1">{step2Errors.shippingState}</p>}
                  </div>
                  <div>
                    <Input
                      placeholder="ZIP Code"
                      value={shippingZip}
                      onChange={e => { setShippingZip(e.target.value); if (step2Errors.shippingZip) setStep2Errors(p => ({ ...p, shippingZip: '' })); }}
                      className={cn(step2Errors.shippingZip && 'border-red-400 focus-visible:ring-red-400')}
                    />
                    {step2Errors.shippingZip && <p className="text-xs text-red-500 mt-1">{step2Errors.shippingZip}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Your Story */}
            {dealStep === 3 && (
              <div className="space-y-5 pt-1">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    What part of First Deal was most instrumental in helping you close this deal?
                  </label>
                  <textarea
                    value={instrumental}
                    onChange={e => { setInstrumental(e.target.value); if (step3Errors.instrumental) setStep3Errors(p => ({ ...p, instrumental: '' })); }}
                    placeholder="Share what made the biggest difference for you..."
                    rows={4}
                    className={cn(
                      'w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none',
                      step3Errors.instrumental && 'border-red-400 focus-visible:ring-red-400',
                    )}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {step3Errors.instrumental
                      ? <p className="text-xs text-red-500">{step3Errors.instrumental}</p>
                      : <span />}
                    <span className={cn('text-xs', instrumental.trim().length >= 20 ? 'text-muted-foreground' : 'text-red-400')}>
                      {instrumental.trim().length} / 20 min
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    What did you struggle with the most along the way?
                  </label>
                  <textarea
                    value={struggled}
                    onChange={e => { setStruggled(e.target.value); if (step3Errors.struggled) setStep3Errors(p => ({ ...p, struggled: '' })); }}
                    placeholder="Be honest -- it helps others who are where you were..."
                    rows={4}
                    className={cn(
                      'w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none',
                      step3Errors.struggled && 'border-red-400 focus-visible:ring-red-400',
                    )}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {step3Errors.struggled
                      ? <p className="text-xs text-red-500">{step3Errors.struggled}</p>
                      : <span />}
                    <span className={cn('text-xs', struggled.trim().length >= 20 ? 'text-muted-foreground' : 'text-red-400')}>
                      {struggled.trim().length} / 20 min
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={cn('flex gap-2 pt-4 border-t border-border', dealStep > 1 ? 'justify-between' : 'justify-end')}>
            {dealStep > 1 && (
              <Button
                variant="outline"
                onClick={() => setDealStep(prev => prev - 1)}
                disabled={submitting}
              >
                Back
              </Button>
            )}
            {dealStep < 3 && (
              <Button
                onClick={() => {
                  if (dealStep === 1) {
                    if (!validateStep1()) { scrollToFirstError(); return; }
                    setDealStep(2);
                  } else {
                    if (!validateStep2()) { scrollToFirstError(); return; }
                    setDealStep(3);
                  }
                }}
                className="gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90"
              >
                Continue
              </Button>
            )}
            {dealStep === 3 && (
              <Button
                onClick={() => { if (!validateStep3()) { scrollToFirstError(); return; } handleDealSubmit(); }}
                disabled={submitting}
                className="gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90"
              >
                {submitting ? 'Saving...' : 'Log My Deal'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Phase5;
